import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useUserPlan, PLAN_CONFIG } from '../hooks/useUserPlan';
import type { CreditTransaction, CreditsState } from '../hooks/useCredits';

/* ─── Toast ─────────────────────────────────────────────────────────────── */
export interface CreditToast {
  id: string;
  message: string;
  type: 'deduct' | 'add' | 'info';
  amount?: number;
}

interface CreditsContextValue extends CreditsState {
  creditLimit: number;
  creditPercent: number;
  daysUntilReset: number;
  hasEnoughCredits: (cost: number) => boolean;
  refetch: () => Promise<void>;
  toasts: CreditToast[];
  fireToast: (msg: string, type: CreditToast['type'], amount?: number) => void;
  dismissToast: (id: string) => void;
}

const CreditsContext = createContext<CreditsContextValue | null>(null);

export function useCreditsContext(): CreditsContextValue {
  const ctx = useContext(CreditsContext);
  if (!ctx) throw new Error('useCreditsContext must be used inside CreditsProvider');
  return ctx;
}

/* ─── Provider ───────────────────────────────────────────────────────────── */
export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { plan, creditLimit } = useUserPlan();
  const [state, setState] = useState<CreditsState>({
    creditsRemaining: 0,
    creditsResetAt: null,
    freeEditsUsed: 0,
    loading: true,
    recentTransactions: [],
    pendingPlan: null,
  });
  const [toasts, setToasts] = useState<CreditToast[]>([]);
  const toastTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const fireToast = useCallback((message: string, type: CreditToast['type'], amount?: number) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev, { id, message, type, amount }]);
    toastTimers.current[id] = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete toastTimers.current[id];
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('credits_remaining, credits_reset_at, free_edits_used, plan, pending_plan, topup_credits')
      .eq('id', user.id)
      .maybeSingle();

    if (!profile) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    let creditsRemaining = profile.credits_remaining ?? 0;
    const topupCredits = profile.topup_credits ?? 0;

    if (false && profile.credits_reset_at && new Date(profile.credits_reset_at) <= new Date()) {
      const activePlan = (profile.pending_plan ?? profile.plan ?? 'free') as keyof typeof PLAN_CONFIG;
      const newLimit = PLAN_CONFIG[activePlan]?.creditLimit ?? 10;
      const nextResetAt = new Date();
      nextResetAt.setMonth(nextResetAt.getMonth() + 1);
      nextResetAt.setDate(1);
      nextResetAt.setHours(0, 0, 0, 0);
      const newTotal = newLimit + topupCredits;
      await supabase.from('profiles').update({
        plan: activePlan,
        credits_remaining: newTotal,
        credits_reset_at: nextResetAt.toISOString(),
        free_edits_used: 0,
        pending_plan: null,
      }).eq('id', user.id);
      creditsRemaining = newTotal;
      const wasDowngrade = profile.pending_plan && profile.pending_plan !== profile.plan;
      await supabase.from('credit_transactions').insert({
        user_id: user.id,
        amount: newLimit,
        action_type: 'monthly_reset',
        description: wasDowngrade
          ? `Monthly reset — plan changed to ${activePlan} · ${newLimit} plan credits added`
          : `Monthly reset — ${newLimit} plan credits added`,
      });
    }

    const { data: transactions } = await supabase
      .from('credit_transactions')
      .select('id, amount, action_type, description, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    setState({
      creditsRemaining,
      creditsResetAt: profile.credits_reset_at,
      freeEditsUsed: profile.free_edits_used ?? 0,
      loading: false,
      recentTransactions: (transactions ?? []) as CreditTransaction[],
      pendingPlan: profile.pending_plan ?? null,
    });
  }, [user, plan]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  /* Realtime subscription — best-effort, context refetch is the primary path */
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`credits-ctx-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`,
      }, () => { fetchCredits(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchCredits]);

  /* Cleanup timers on unmount */
  useEffect(() => {
    const timers = toastTimers.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  const hasEnoughCredits = useCallback((cost: number) => state.creditsRemaining >= cost, [state.creditsRemaining]);
  const creditPercent = creditLimit > 0 ? Math.min((state.creditsRemaining / creditLimit) * 100, 100) : 100;
  const daysUntilReset = state.creditsResetAt
    ? Math.max(0, Math.ceil((new Date(state.creditsResetAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <CreditsContext.Provider value={{
      ...state,
      creditLimit,
      creditPercent,
      daysUntilReset,
      hasEnoughCredits,
      refetch: fetchCredits,
      toasts,
      fireToast,
      dismissToast,
    }}>
      {children}
    </CreditsContext.Provider>
  );
}
